import { formatCurrency } from "@gigpro/utils";
import { getDefaultRecurrence, RecurrenceWithRepeat } from "@gigpro/utils/datetime";
import { LocationManagerRoleEnum } from "@sidegigllc/api/client";
import { locationAddressInputSchema, locationAddressSchema } from "api/resources/LocationAddress";
import { fileAttachmentSchema, gigTypeSchema, type TipPolicy, tipPolicySchema } from "api/resources/shared";
import { skillWithDefaultsSchema } from "api/resources/skills";
import { gigTemplateSchema } from "api/resources/templates";
import * as z from "zod/mini";

export type Section = keyof PostGigData;
const sections = ["shift", "recurrence", "pros", "location", "manager", "summary", "scope", "attachments"] as const;

export const SectionShift = z.object({
  baseDate: z.date(),
  startTime: z.date(),
  endTime: z.date(),
  unpaidBreakMinutes: z.optional(z.number()),
  timezone: z.string(),
  templateId: z.optional(z.coerce.number().check(z.nonnegative())),
});

export type SectionShift = z.infer<typeof SectionShift>;

const recurrenceSectionSchema = z._default(RecurrenceWithRepeat, getDefaultRecurrence(new Date()));
export type SectionRecurrence = z.infer<typeof recurrenceSectionSchema>;

// `uniform` is inherited from gigTemplateSchema — it now persists on the template (CORE-6358) as well
// as riding into the gig-create submit (CORE-6325), so it no longer needs a Pro-only override.
export const proPostSchema = z.partial(gigTemplateSchema, { id: true, name: true, location_id: true });
export type Pro = z.infer<typeof proPostSchema>;

export const TIP_POLICY_REQUIRED_MESSAGE = "Select whether this shift includes tips";

/**
 * superRefine for the per-skill "Includes tips" rule (WORK-8452): when the selected skill's `tips_required`
 * is on, `tip_policy` must be answered (any of Yes / No / Pooled). The flag is resolved server-side and read
 * off the skill, so apply this only where the skill is known (e.g. the position form's skill-aware schema
 * transform).
 */
export function tipPolicyRefinement(tipsRequired: boolean | undefined) {
  return (data: Pick<Pro, "tip_policy">, ctx: z.core.$RefinementCtx) => {
    if (!tipsRequired || data.tip_policy != null) return;
    ctx.addIssue({ code: "custom", message: TIP_POLICY_REQUIRED_MESSAGE, path: ["tip_policy"] });
  };
}

export const scopeSchema = z._default(
  z.object({
    autoPromoteToMarketplace: z._default(z.boolean(), false),
    gigType: gigTypeSchema,
    selectedPros: z.optional(z.array(z.number()).check(z.minLength(1, "Please add professionals"))),
    // Names of the picked pros, carried from the picker so later steps can name them.
    selectedProNames: z.optional(z.record(z.string(), z.string())),
  }),
  { autoPromoteToMarketplace: false, gigType: "MARKETPLACE" },
);
export type Scope = z.infer<typeof scopeSchema>;

/** The post form's wage bounds; the AI chat's canvas checks a draft against the same ones, in the same words. */
export const MAX_WAGE_AMOUNT = 75;
export const minimumWageMessage = (minWage: number) => `Hourly wage must be at least ${formatCurrency(minWage)}/hr`;

export const proSectionSchema = z.array(
  z.object({
    count: z.number(),
    pro: proPostSchema,
    manageableGigs: z.optional(z.array(z.number())),
    wageOverride: z.optional(
      z
        .object({
          key: z.enum(["recommended", "avg", "custom"]),
          wage: z.number().check(z.lte(MAX_WAGE_AMOUNT)),
          skill: skillWithDefaultsSchema,
        })
        .check(
          z.superRefine((data, ctx) => {
            const minWage = data.skill.minimum_wage_amount ?? 15;
            if (data.wage < minWage) {
              ctx.addIssue({
                code: "custom",
                message: minimumWageMessage(minWage),
                path: ["wage"],
              });
            }
          }),
        ),
    ),
    // Per-post "Does this shift include tips?" answer, like `wageOverride`: it changes only the gig being
    // posted, never the position or template. `undefined` means "use the position's answer".
    tipPolicyOverride: z.optional(z.nullable(tipPolicySchema)),
    scope: z.optional(scopeSchema),
  }),
);
export type SectionPros = z.infer<typeof proSectionSchema>;

/** The tips answer a gig is posted with: the per-post override when set, otherwise the position's answer. */
export const effectiveTipPolicy = ({
  pro,
  tipPolicyOverride,
}: Pick<SectionPros[number], "pro" | "tipPolicyOverride">): TipPolicy | null =>
  tipPolicyOverride === undefined ? (pro.tip_policy ?? null) : tipPolicyOverride;

/**
 * Whether a position entry is about to post without a required tips answer: its skill is in
 * `tipsRequiredSkillIds` and neither the per-post override nor the position answered. A template or copied
 * gig saved before the skill became tips-required can reach the post step unanswered, so the post forms
 * check this too, not only the position form.
 */
export const tipsAnswerMissing = (
  entry: Pick<SectionPros[number], "pro" | "tipPolicyOverride">,
  tipsRequiredSkillIds: ReadonlySet<number>,
): boolean => tipsRequiredSkillIds.has(entry.pro.skill_id) && effectiveTipPolicy(entry) === null;

const locationSectionSchema = z.object({
  address: locationAddressSchema,
  parkingOverrides: z.optional(
    z.object({
      ...z.pick(locationAddressInputSchema, { parking_expense_type: true, parking_instructions: true }).shape,
      ...z.pick(locationAddressSchema, { attachments: true }).shape,
    }),
  ),
});
export type SectionLocation = z.infer<typeof locationSectionSchema>;

// Keep the manager section as a normalized interface
export const managerSectionSchema = z.object({
  id: z.number(),
  fullname: z.string(),
  phone_number: z.string(),
  role: z.optional(z.enum(LocationManagerRoleEnum)),
  // Add field to indicate if this comes from LocationManager
  location_manager_id: z.optional(z.number()), // Only set when selected from LocationManager
  location_id: z.optional(z.number()), // The location `location_manager_id` was resolved for
  location_invite_id: z.optional(z.number()), // Only set when selected from LocationManager and pending
});

export type SectionManager = z.infer<typeof managerSectionSchema>;

const summarySchema = z.object({
  posting: z.optional(z.boolean()),
  posted: z.optional(z.boolean()),
  error: z.optional(
    z.object({
      type: z.enum(["api", "local"]),
      message: z.string(),
      section: z.optional(z.enum(sections)),
    }),
  ),
});
export type Summary = z.infer<typeof summarySchema>;

export const postGigDataSchema = z.object({
  shift: SectionShift,
  recurrence: recurrenceSectionSchema,
  pros: proSectionSchema,
  location: locationSectionSchema,
  manager: managerSectionSchema,
  summary: z.optional(summarySchema),
  attachments: z.optional(z.array(fileAttachmentSchema)),
});
export type PostGigData = z.infer<typeof postGigDataSchema>;
